import { ThemeDefinition, ThemeMode, ResolvedMode, USWDSColorFamily } from './types';
import { applyThemeTokens } from './design-token-bridge';
import * as api from '../services/api-client';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MODE_STORAGE_KEY = 'coreflow-theme-mode';
const THEME_ID_STORAGE_KEY = 'coreflow-theme-id';

const REQUIRED_FAMILIES: USWDSColorFamily[] = [
  'primary',
  'secondary',
  'accent-cool',
  'accent-warm',
  'base',
  'error',
  'warning',
  'success',
  'info',
];

// ---------------------------------------------------------------------------
// Theme Registry
// ---------------------------------------------------------------------------

/** Registry holding all available themes and the active/system defaults */
export interface ThemeRegistry {
  readonly themes: Map<string, ThemeDefinition>;
  readonly activeThemeId: string;
  readonly systemThemeId: string;
}

// ---------------------------------------------------------------------------
// Theme Validation
// ---------------------------------------------------------------------------

/** Validates a theme definition has all required color families */
export function validateTheme(theme: ThemeDefinition): { valid: boolean; missing: string[] } {
  const missing = REQUIRED_FAMILIES.filter((f) => !theme.colors[f]);
  return { valid: missing.length === 0, missing };
}

// ---------------------------------------------------------------------------
// Mode Persistence and Resolution
// ---------------------------------------------------------------------------

/** Reads persisted theme mode preference from localStorage */
export function getPersistedMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(MODE_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    /* localStorage unavailable */
  }
  return 'system';
}

/** Persists theme mode to localStorage */
export function persistMode(mode: ThemeMode): void {
  try {
    localStorage.setItem(MODE_STORAGE_KEY, mode);
  } catch {
    /* localStorage unavailable */
  }
}

/** Resolves 'system' to the actual OS preference */
export function resolveMode(mode: ThemeMode): ResolvedMode {
  if (mode === 'light' || mode === 'dark') return mode;
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

/** Applies the resolved mode class to the document root */
export function applyMode(resolved: ResolvedMode): void {
  const root = document.documentElement;
  root.classList.remove('theme-light', 'theme-dark');
  root.classList.add(`theme-${resolved}`);
}

/** Cycles through mode states: light → dark → system → light */
export function cycleMode(current: ThemeMode): ThemeMode {
  const cycle: ThemeMode[] = ['light', 'dark', 'system'];
  const idx = cycle.indexOf(current);
  return cycle[(idx + 1) % cycle.length]!;
}

// ---------------------------------------------------------------------------
// Theme ID Persistence
// ---------------------------------------------------------------------------

/** Persists the active theme ID to localStorage */
export function persistThemeId(id: string): void {
  try {
    localStorage.setItem(THEME_ID_STORAGE_KEY, id);
  } catch {
    /* localStorage unavailable */
  }
}

/** Reads the persisted theme ID from localStorage */
export function getPersistedThemeId(): string | null {
  try {
    return localStorage.getItem(THEME_ID_STORAGE_KEY);
  } catch {
    /* localStorage unavailable */
    return null;
  }
}

/** Clears the persisted theme ID (used when the stored ID is invalid) */
export function clearPersistedThemeId(): void {
  try {
    localStorage.removeItem(THEME_ID_STORAGE_KEY);
  } catch {
    /* localStorage unavailable */
  }
}

// ---------------------------------------------------------------------------
// Theme Loading via Bridge
// ---------------------------------------------------------------------------

/**
 * Loads a theme by ID from the registry and applies it via the Design Token Bridge.
 * Uses requestAnimationFrame to batch CSS property changes within a single frame,
 * preventing visual flicker during theme swaps.
 *
 * If the ID is not found in the registry, falls back to the system theme and
 * clears the invalid persisted value.
 */
export function loadThemeById(id: string, registry: ThemeRegistry, mode: ResolvedMode): void {
  const theme = registry.themes.get(id);

  if (!theme) {
    // Invalid ID — fall back to system theme and clear persisted value
    clearPersistedThemeId();
    const fallback = registry.themes.get(registry.systemThemeId);
    if (fallback) {
      applyThemeTokens(fallback, mode);
    }
    return;
  }

  const validation = validateTheme(theme);
  if (!validation.valid) {
    console.warn(
      `[ThemeEngine] Theme "${id}" is missing color families: ${validation.missing.join(', ')}. Falling back to system theme.`
    );
    clearPersistedThemeId();
    const fallback = registry.themes.get(registry.systemThemeId);
    if (fallback) {
      applyThemeTokens(fallback, mode);
    }
    return;
  }

  applyThemeTokens(theme, mode);
}

// ---------------------------------------------------------------------------
// Fetch Themes from BFF
// ---------------------------------------------------------------------------

/** Response shape from GET /v1/admin/themes */
interface ThemesListResponse {
  readonly data: ThemeDefinition[];
}

/**
 * Fetches available themes from the BFF.
 * Returns an array of ThemeDefinitions that can populate a ThemeRegistry.
 */
export async function fetchThemes(): Promise<ThemeDefinition[]> {
  try {
    const response = await api.get<ThemesListResponse>('/v1/admin/themes');
    return response.data;
  } catch (error) {
    console.warn('[ThemeEngine] Failed to fetch themes from BFF:', error);
    return [];
  }
}

// ---------------------------------------------------------------------------
// Legacy loadTheme (enhanced to also apply bridge tokens)
// ---------------------------------------------------------------------------

/** Color grade keys in a ColorGrade object */
const COLOR_GRADES = ['lightest', 'lighter', 'light', 'base', 'dark', 'darker', 'darkest'] as const;

/**
 * Loads a ThemeDefinition by setting CSS custom properties on document.documentElement.
 *
 * This function sets both:
 * 1. Legacy --coreflow-* properties for backward compatibility
 * 2. Tailwind CSS token variables via the Design Token Bridge
 *
 * The mode parameter defaults to the current resolved mode from the document root class.
 */
export function loadTheme(theme: ThemeDefinition, mode?: ResolvedMode): void {
  const root = document.documentElement;

  // Resolve mode from document class if not provided
  const resolvedMode: ResolvedMode = mode ?? (root.classList.contains('theme-dark') ? 'dark' : 'light');

  // Apply via Design Token Bridge (Tailwind CSS variables)
  applyThemeTokens(theme, resolvedMode);

  // Set legacy color custom properties for every family and grade
  for (const family of REQUIRED_FAMILIES) {
    const colorGrade = theme.colors[family];
    if (!colorGrade) continue;
    for (const grade of COLOR_GRADES) {
      root.style.setProperty(`--coreflow-${family}-${grade}`, colorGrade[grade]);
    }
  }

  // Set typography custom properties
  root.style.setProperty('--coreflow-font-body', theme.typography.bodyFamily);
  root.style.setProperty('--coreflow-font-heading', theme.typography.headingFamily);
  root.style.setProperty('--coreflow-font-mono', theme.typography.monoFamily);
  root.style.setProperty('--coreflow-font-size-body', theme.typography.bodySize);
  root.style.setProperty('--coreflow-line-height', theme.typography.lineHeight);

  // Set focus custom properties
  root.style.setProperty('--coreflow-focus-color', theme.focus.color);
  root.style.setProperty('--coreflow-focus-offset', theme.focus.offset);
  root.style.setProperty('--coreflow-focus-width', theme.focus.width);
}
